import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from '@/shared/hooks/use-toast';
import { useUpdateOverlay } from '../../hooks/hooks';

interface ImageEditFormProps {
  hotspotId: number;
  overlayId: number;
  currentTitle: string;
  currentImageUrl: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ImageEditForm: React.FC<ImageEditFormProps> = ({
  hotspotId,
  overlayId,
  currentTitle,
  currentImageUrl,
  onSuccess,
  onCancel,
}) => {
  const [title, setTitle] = useState<string>(currentTitle);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: updateOverlay, isPending: isUpdating } = useUpdateOverlay();

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();

      const isMobile = /iphone|ipod|android.*mobile|windows phone|blackberry|nokia/i.test(
        userAgent
      );

      const isTablet =
        /ipad|android(?!.*mobile)|tablet|kindle|silk|playbook|bb10|surface|touch/i.test(
          userAgent
        ) ||
        (navigator.maxTouchPoints > 1 && /windows/i.test(userAgent)) ||
        (/windows/i.test(userAgent) && 'ontouchstart' in window) ||
        (navigator.maxTouchPoints > 1 && !isMobile);

      const isWindowsTouch = 'ontouchstart' in window && /windows/i.test(userAgent);

      const hasCameraSupport =
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;

      const shouldShowCamera =
        isMobile ||
        isTablet ||
        isWindowsTouch ||
        (navigator.maxTouchPoints > 0 && hasCameraSupport);

      setIsMobileOrTablet(shouldShowCamera);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const handleFileSelect = (file: File | null) => {
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraCaptureWithFallback = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      console.warn('Camera input not available, falling back to file upload');
      toast({
        title: 'Camera not available',
        description: 'Using file upload instead. You can select photos from your camera roll.',
        variant: 'default',
        className: 'bg-blue-500 text-white',
      });
      handleUploadClick();
    }
  };

  const handleRemoveNewPhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    // Check if anything has changed
    const titleChanged = title !== currentTitle;
    const imageChanged = selectedFile !== null;

    if (!titleChanged && !imageChanged) {
      toast({
        title: 'No changes',
        description: 'No changes were made to save.',
        variant: 'default',
        className: 'bg-blue-500 text-white',
      });
      return;
    }

    try {
      const updateData: { title?: string; photo?: File } = {};

      if (titleChanged) {
        updateData.title = title;
      }

      if (imageChanged) {
        updateData.photo = selectedFile!;
      }

      await updateOverlay({
        hotspotId,
        overlayId,
        data: updateData,
      });

      toast({
        title: 'Success',
        description: 'Image updated successfully!',
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error updating image:', error);
      toast({
        title: 'Error',
        description: 'Failed to update image. Please try again.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Title edit field */}
      <div className="space-y-2">
        <Label htmlFor="edit-image-title">Title</Label>
        <Input
          id="edit-image-title"
          type="text"
          placeholder="Enter a title for this image..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isUpdating}
        />
      </div>

      {/* Current image display */}
      <div className="space-y-2">
        <Label>Current Image</Label>
        <div className="rounded-lg overflow-hidden border">
          <img
            src={currentImageUrl}
            alt={currentTitle}
            className="w-full h-auto max-h-64 object-contain bg-gray-50"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      </div>

      {/* New image selection (if any) */}
      {selectedFile ? (
        <div className="space-y-2">
          <Label>New Image Preview</Label>
          <Card className="relative overflow-hidden">
            <img src={previewUrl || ''} alt="New preview" className="w-full h-64 object-cover" />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={handleRemoveNewPhoto}
            >
              <X className="h-4 w-4" />
            </Button>
          </Card>
          <div className="text-sm text-muted-foreground">
            <p>New file: {selectedFile.name}</p>
            <p>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Replace Image (Optional)</Label>
          <div className="space-y-2">
            {isMobileOrTablet && (
              <Card
                className="p-4 cursor-pointer hover:border-primary transition-colors"
                onClick={handleCameraCaptureWithFallback}
              >
                <div className="flex items-center space-x-2">
                  <Camera className="w-8 h-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">Take New Photo</h3>
                    <p className="text-sm text-muted-foreground">
                      Replace with a new photo using your camera
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <Card
              className="p-4 cursor-pointer hover:border-primary transition-colors"
              onClick={handleUploadClick}
            >
              <div className="flex items-center space-x-2">
                <Upload className="w-8 h-8 text-primary" />
                <div>
                  <h3 className="font-semibold">Upload New Image</h3>
                  <p className="text-sm text-muted-foreground">
                    Replace with an image from your gallery or files
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={isUpdating}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isUpdating}>
          {isUpdating ? 'Updating...' : 'Update Image'}
        </Button>
      </div>
    </div>
  );
};

export default ImageEditForm;
